/* eslint-disable warp-drive/no-legacy-request-patterns */
import { helper } from '@ember/component/helper';
import { getGradeForDisplay } from '@linc-technologies/linced-common/utils/grades';

/**
 * Resolves a section's assessment-data component into groups for rendering.
 *
 * Usage (live): {{reporting/assessment-groups comp.modules assessmentGrades store}}
 * Usage (snapshot-first): {{reporting/assessment-groups comp.modules assessmentGrades store snapshotGroups}}
 *
 * When `snapshotGroups` is provided (non-null, even if empty) it is returned
 * verbatim — this is the publish-snapshot path, where the backend has already
 * resolved each module's label and the student's awarded grade (HERO-13256).
 * An empty array means the section had no grades at publish time and must stay
 * frozen, never falling through to live store data. Falls back to live
 * resolution only when no snapshot slot was provided: pick the latest grade per (personId,
 * moduleId), route each grade through getGradeForDisplay so SELECT /
 * MULTIPLE_SELECT / ICON_SELECT render their schedule labels (HERO-13256
 * fix), group modules by parent assessment, preserve order.
 */
export default helper(function assessmentGroups([
  modules,
  grades,
  store,
  gradeScope,
  yearStartUnix,
  yearEndUnix,
  snapshotGroups,
]) {
  if (snapshotGroups != null) {
    return snapshotGroups;
  }

  if (!modules || !modules.length || !store) return [];

  let moduleIds = modules
    .map((m) => (typeof m === 'string' ? m : m.id))
    .filter(Boolean);

  // Scoping to the current academic year is the default: only grades awarded
  // within that window are eligible. Opting into 'latest' makes any grade
  // eligible so the most recent one wins regardless of date.
  let limitToYear =
    gradeScope !== 'latest' && yearStartUnix != null && yearEndUnix != null;

  // Build grade lookup: moduleId → grade record (latest eligible grade)
  let gradeMap = {};
  if (grades) {
    grades.forEach((grade) => {
      let moduleRef = grade.belongsTo('assessmentModuleId');
      let moduleId = moduleRef ? moduleRef.id() : null;
      if (!moduleId || !moduleIds.includes(moduleId)) return;

      let createTime = grade.get('createTime');
      if (
        limitToYear &&
        (createTime == null ||
          createTime < yearStartUnix ||
          createTime > yearEndUnix)
      ) {
        return;
      }

      // >= so the later grade wins a createTime tie: imports stamp createTime
      // with the assessment date, so re-imports tie, and the API returns
      // history oldest-first by insertion, making the latest import last.
      if (
        !gradeMap[moduleId] ||
        createTime >= gradeMap[moduleId].get('createTime')
      ) {
        gradeMap[moduleId] = grade;
      }
    });
  }

  // Group modules by parent assessment, preserving order
  let groups = {};
  let order = [];

  moduleIds.forEach((moduleId) => {
    let moduleRecord = store.peekRecord('assessments/module', moduleId);
    if (!moduleRecord) return;

    // Hide modules with no grade: skip them entirely rather than rendering an
    // "N/A" row. An assessment group with no graded modules is never created.
    let grade = gradeMap[moduleId];
    let awardedValue = grade
      ? getGradeForDisplay(moduleRecord, grade, true)
      : null;
    if (awardedValue == null || awardedValue === '') return;

    let assessmentRef = moduleRecord.belongsTo('assessmentId');
    let assessmentId = assessmentRef ? assessmentRef.id() : 'unknown';
    let assessment = assessmentRef ? assessmentRef.value() : null;
    let assessmentLabel = assessment ? assessment.get('label') : 'Assessment';

    if (!groups[assessmentId]) {
      groups[assessmentId] = { assessmentLabel, modules: [] };
      order.push(assessmentId);
    }

    groups[assessmentId].modules.push({
      label: moduleRecord.get('label'),
      description: moduleRecord.get('description'),
      awarded: String(awardedValue),
    });
  });

  return order.map((id) => groups[id]);
});
